// src/components/player-profile/InfoTab.js
//
// Extracted from inline closure in PlayerProfileModal.js per IMPORTANT
// batch 8 commit 8c (refactor only — no behavior change).
//
// Closure-→-prop conversion: 3 closure-captured variables promoted to
// declared props (player, performance, isPitcher).
//
// Helper handling:
//   - formatHeight migrated to shared services/playerUtils in 8k.2a
//     (default missingValue 'N/A' preserves InfoTab's existing 'N/A'
//     idiom; cross-codebase 4-file drift was reconciled per Bible
//     §14.test-infra.4 file-count promotion threshold)
//   - formatAvg + formatEra rename-alias wrappers deleted in 8k.2b;
//     call sites now use canonical formatBattingAvg / formatERA
//     directly from services/statFormatUtils.
import React from 'react';
import { Activity, Zap, Target, TrendingUp, Flame, Shield, DollarSign } from 'lucide-react';
import { formatBattingAvg, formatERA } from '../../services/statFormatUtils';
import { formatContractDisplay } from '../../services/contractUtils';
import { formatHeight } from '../../services/playerUtils';
import { dynastyTheme } from '../../services/colorService';

const InfoTab = ({ player, performance, isPitcher }) => (
  <div className="space-y-4">
    <div className={`${dynastyTheme.components.card.base} p-4`}>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className={dynastyTheme.classes.text.neutralLight}>Height:</span>
          <span className={`${dynastyTheme.classes.text.white} ml-2`}>
            {formatHeight(player.info?.height_inches)}
          </span>
        </div>
        <div>
          <span className={dynastyTheme.classes.text.neutralLight}>Weight:</span>
          <span className={`${dynastyTheme.classes.text.white} ml-2`}>
            {player.info?.weight_pounds || 'N/A'} lbs
          </span>
        </div>
        <div>
          <span className={dynastyTheme.classes.text.neutralLight}>Jersey:</span>
          <span className={`${dynastyTheme.classes.text.white} ml-2`}>
            #{player.info?.jersey_number || 'N/A'}
          </span>
        </div>
        <div>
          <span className={dynastyTheme.classes.text.neutralLight}>Birthdate:</span>
          <span className={`${dynastyTheme.classes.text.white} ml-2`}>
            {player.info?.birthdate ? new Date(player.info.birthdate).toLocaleDateString() : 'N/A'}
          </span>
        </div>
      </div>
    </div>

    <div className={`${dynastyTheme.components.card.base} p-4`}>
      <h4 className={`${dynastyTheme.classes.text.primary} font-semibold mb-3 flex items-center`}>
        <Activity className="w-4 h-4 mr-2" />
        Performance Status
      </h4>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <span className={dynastyTheme.classes.text.neutralLight}>Current Form:</span>
          <span className={`ml-2 font-medium ${
            performance.status === 'hot' ? dynastyTheme.classes.text.success :
            performance.status === 'cold' ? dynastyTheme.classes.text.error :
            dynastyTheme.classes.text.white
          }`}>
            {performance.text}
          </span>
        </div>
        <div>
          <span className={dynastyTheme.classes.text.neutralLight}>Last 14 Days:</span>
          <span className={`${dynastyTheme.classes.text.white} ml-2`}>
            {(() => {
              const rolling = player?.stats?.rolling_14_day;
              return rolling ?
                (isPitcher() ?
                  `${formatERA(rolling.pitching?.era)} ERA` :
                  rolling.hitting?.ops ?
                    `${formatBattingAvg(rolling.hitting.ops)} OPS` :
                    `${formatBattingAvg(rolling.hitting?.batting_avg)} AVG`
                ) : 'No data';
            })()}
          </span>
        </div>
      </div>
    </div>

    {/* Season Analytics Section */}
    {player?.stats?.season && (
      <div className={`${dynastyTheme.components.card.base} p-4`}>
        <h4 className={`${dynastyTheme.classes.text.primary} font-semibold mb-3 flex items-center`}>
          <Zap className="w-4 h-4 mr-2" />
          Key Performance Metrics
        </h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          {!isPitcher() ? (
            <>
              <div className="flex items-center space-x-2">
                <Target className="w-4 h-4 text-yellow-400" />
                <div>
                  <span className={dynastyTheme.classes.text.neutralLight}>Power:</span>
                  <span className={`${dynastyTheme.classes.text.white} ml-2 font-semibold`}>
                    {player.stats.season.hitting?.home_runs || 0} HR, {formatBattingAvg(player.stats.season.hitting?.slg || 0)} SLG
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-4 h-4 text-green-400" />
                <div>
                  <span className={dynastyTheme.classes.text.neutralLight}>Contact:</span>
                  <span className={`${dynastyTheme.classes.text.white} ml-2 font-semibold`}>
                    {(() => {
                      const season = player.stats.season;
                      const k = season.hitting?.strikeouts || 0;
                      const ab = season.hitting?.at_bats || 0;
                      return k && ab ? `${(100 - (k / ab * 100)).toFixed(0)}% Contact` : 'N/A';
                    })()}
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Flame className="w-4 h-4 text-orange-400" />
                <div>
                  <span className={dynastyTheme.classes.text.neutralLight}>Run Production:</span>
                  <span className={`${dynastyTheme.classes.text.white} ml-2 font-semibold`}>
                    {(player.stats.season.hitting?.runs || 0) + (player.stats.season.hitting?.rbi || 0)} R+RBI
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4 text-blue-400" />
                <div>
                  <span className={dynastyTheme.classes.text.neutralLight}>Discipline:</span>
                  <span className={`${dynastyTheme.classes.text.white} ml-2 font-semibold`}>
                    {(() => {
                      const season = player.stats.season;
                      const bb = season.hitting?.walks || 0;
                      const k = season.hitting?.strikeouts || 0;
                      return bb && k ? `${(bb / k).toFixed(2)} BB/K` : 'N/A';
                    })()}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center space-x-2">
                <Flame className="w-4 h-4 text-orange-400" />
                <div>
                  <span className={dynastyTheme.classes.text.neutralLight}>Dominance:</span>
                  <span className={`${dynastyTheme.classes.text.white} ml-2 font-semibold`}>
                    {(() => {
                      const season = player.stats.season;
                      const ip = season.pitching?.innings_pitched || 0;
                      const k = season.pitching?.strikeouts_pitched || 0;
                      return ip && k ? `${((k * 9) / ip).toFixed(1)} K/9` : 'N/A';
                    })()}
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4 text-blue-400" />
                <div>
                  <span className={dynastyTheme.classes.text.neutralLight}>Command:</span>
                  <span className={`${dynastyTheme.classes.text.white} ml-2 font-semibold`}>
                    {(() => {
                      const season = player.stats.season;
                      const ip = season.pitching?.innings_pitched || 0;
                      const bb = season.pitching?.walks_allowed || 0;
                      return ip && bb >= 0 ? `${((bb * 9) / ip).toFixed(1)} BB/9` : 'N/A';
                    })()}
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Target className="w-4 h-4 text-yellow-400" />
                <div>
                  <span className={dynastyTheme.classes.text.neutralLight}>Efficiency:</span>
                  <span className={`${dynastyTheme.classes.text.white} ml-2 font-semibold`}>
                    {(() => {
                      const season = player.stats.season;
                      const ip = season.pitching?.innings_pitched || 0;
                      const starts = season.pitching?.games_played || 0;
                      return ip && starts ? `${(ip / starts).toFixed(1)} IP/Start` : 'N/A';
                    })()}
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-4 h-4 text-green-400" />
                <div>
                  <span className={dynastyTheme.classes.text.neutralLight}>Win Rate:</span>
                  <span className={`${dynastyTheme.classes.text.white} ml-2 font-semibold`}>
                    {(() => {
                      const season = player.stats.season;
                      const wins = season.pitching?.wins || 0;
                      const losses = season.pitching?.losses || 0;
                      const total = wins + losses;
                      return total > 0 ? `${((wins / total) * 100).toFixed(0)}%` : '0%';
                    })()}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    )}

    {(() => {
      const infoIsOffseason = ['setup', 'draft', 'finalizing'].includes(player.league_status);
      const infoContract = infoIsOffseason && player.projected_contract
          ? player.projected_contract
          : player.league_context?.financial;
      return infoContract?.contract_salary ? (
      <div className={`${dynastyTheme.components.card.base} p-4`}>
        <h4 className={`${dynastyTheme.classes.text.primary} font-semibold mb-3 flex items-center`}>
          <DollarSign className="w-4 h-4 mr-2" />
          Contract Details
        </h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className={dynastyTheme.classes.text.neutralLight}>Salary:</span>
            <span className={`${dynastyTheme.classes.text.success} ml-2 font-semibold`}>
              ${Math.round(infoContract.contract_salary)}M
            </span>
          </div>
          <div>
            <span className={dynastyTheme.classes.text.neutralLight}>Contract:</span>
            <span className={`${dynastyTheme.classes.text.white} ml-2`}>
              {formatContractDisplay(infoContract.contract_type, infoContract.contract_years)}
            </span>
          </div>
        </div>
      </div>
      ) : null;
    })()}
  </div>
);

export default InfoTab;
